import React, { useState, useMemo } from 'react';
import { Calendar as CalendarIcon, CloudSnow, Loader2, ArrowRight, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTasks } from '../../context/TasksContext';

const REASONS = [
  { value: 'weather',          label: 'Weather' },
  { value: 'customer_request', label: 'Customer request' },
  { value: 'technician',       label: 'Technician' },
  { value: 'office',           label: 'Office' },
  { value: 'system',           label: 'System' },
];

const formatLongDate = (d) =>
  d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const todayIsoLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * BulkRescheduleModal
 *
 * Sprint 2 — Dispatch "Move Day…" / "Move Selected Jobs…" workflow.
 *
 * Contract:
 *   - Reuses bulkRescheduleTasks (which delegates to canonical rescheduleTask).
 *   - Preserves each task's time-of-day, duration, assignedTo, jobMode, status.
 *   - Default reason is 'weather' — the most common real-world bulk case.
 *   - Customer name preview (first 5 + remaining count) shown before confirm.
 *   - Emits the same job_rescheduled history events as individual reschedules.
 *
 * Props:
 *   isOpen, onClose, fromDate (Date), tasks (array of full task objects),
 *   leadsById (map of leadId → lead, for customer-name preview),
 *   label ('Move Day' | 'Move Selected Jobs'),
 *   onMoved (callback fired after a successful move).
 */
const BulkRescheduleModal = ({
  isOpen, onClose,
  fromDate, tasks: targetTasks, leadsById,
  label = 'Move Day',
  onMoved,
}) => {
  const { bulkRescheduleTasks } = useTasks();

  // Default to "tomorrow" relative to fromDate — the typical weather shift.
  const defaultDate = useMemo(() => {
    const base = fromDate ? new Date(fromDate) : new Date();
    base.setDate(base.getDate() + 1);
    return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;
  }, [fromDate]);

  const [targetDateStr, setTargetDateStr] = useState(defaultDate);
  const [reason, setReason]               = useState('weather');
  const [note, setNote]                   = useState('');
  const [submitting, setSubmitting]       = useState(false);

  // Reset state each time the modal reopens for a new set.
  React.useEffect(() => {
    if (isOpen) {
      setTargetDateStr(defaultDate);
      setReason('weather');
      setNote('');
      setSubmitting(false);
    }
  }, [isOpen, defaultDate]);

  // ── Customer name preview ───────────────────────────────────────────────
  const customerPreview = useMemo(() => {
    if (!Array.isArray(targetTasks) || targetTasks.length === 0) {
      return { names: [], remaining: 0 };
    }
    const seen = new Set();
    const names = [];
    targetTasks.forEach((t) => {
      const lead = leadsById?.[t.leadId];
      const name = (lead?.fullName || lead?.name || t.clientName || '').trim() || 'Customer';
      if (seen.has(name)) return;
      seen.add(name);
      if (names.length < 5) names.push(name);
    });
    return { names, remaining: Math.max(0, seen.size - names.length) };
  }, [targetTasks, leadsById]);

  const fromLabel = useMemo(
    () => (fromDate ? formatLongDate(fromDate) : 'this day'),
    [fromDate],
  );

  const targetDateObj = useMemo(() => {
    if (!targetDateStr) return null;
    const [y, m, d] = targetDateStr.split('-').map((v) => parseInt(v, 10));
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }, [targetDateStr]);

  const targetLabel = useMemo(
    () => (targetDateObj ? formatLongDate(targetDateObj) : '—'),
    [targetDateObj],
  );

  const sameDay = useMemo(() => {
    if (!fromDate || !targetDateObj) return false;
    return (
      fromDate.getFullYear() === targetDateObj.getFullYear() &&
      fromDate.getMonth()    === targetDateObj.getMonth() &&
      fromDate.getDate()     === targetDateObj.getDate()
    );
  }, [fromDate, targetDateObj]);

  const count = targetTasks?.length || 0;
  const disabled = submitting || !targetDateObj || sameDay || count === 0;

  const handleConfirm = async () => {
    if (!targetDateObj || count === 0) return;
    setSubmitting(true);
    try {
      const taskIds = targetTasks.map((t) => t.id);
      const result = await bulkRescheduleTasks(taskIds, {
        targetDate: targetDateObj,
        reason,
        note: note.trim() || null,
      });
      const moved   = result.moved.length;
      const skipped = result.skipped.length;
      const failed  = result.failed.length;
      if (moved > 0) {
        toast.success(
          `Moved ${moved} job${moved === 1 ? '' : 's'} to ${targetLabel}`,
          skipped + failed > 0
            ? { description: `${skipped} skipped${failed > 0 ? `, ${failed} failed` : ''}` }
            : undefined,
        );
      } else if (failed > 0) {
        toast.error(`${failed} move${failed === 1 ? '' : 's'} failed`);
      } else {
        toast.info('No jobs needed to move');
      }
      onMoved?.(result);
      onClose();
    } catch (err) {
      console.error('[BulkReschedule] failed:', err);
      toast.error(err?.message || 'Bulk reschedule failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-reschedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CloudSnow className="h-5 w-5 text-sky-600" />
            {label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* From → To summary */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-500">From</span>
              <span className="font-semibold text-gray-900">{fromLabel}</span>
            </div>
            <div className="flex items-center justify-center my-1.5">
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-500">To</span>
              <span className={`font-semibold ${sameDay ? 'text-red-600' : 'text-gray-900'}`}>
                {targetLabel}
              </span>
            </div>
            {sameDay && (
              <p className="text-[11px] text-red-600 mt-1.5" data-testid="bulk-reschedule-same-day-warning">
                Pick a different day to enable the move.
              </p>
            )}
          </div>

          {/* Date picker */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />
              Move to date
            </label>
            <input
              type="date"
              value={targetDateStr}
              min={todayIsoLocal()}
              onChange={(e) => setTargetDateStr(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
              data-testid="bulk-reschedule-date"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-emerald-400"
              data-testid="bulk-reschedule-reason"
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Optional note */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Note <span className="text-gray-300 font-normal">— optional, shown on the customer timeline</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 280))}
              rows={2}
              placeholder="e.g. Heavy rain forecast — moving all routes to tomorrow"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400 resize-none"
              data-testid="bulk-reschedule-note"
            />
            <p className="text-[10px] text-gray-400 mt-0.5 text-right">{note.length}/280</p>
          </div>

          {/* Customer preview */}
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5"
            data-testid="bulk-reschedule-customer-preview"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Users className="h-3.5 w-3.5 text-emerald-700" />
              <span className="text-xs font-semibold text-emerald-900">
                {count} job{count === 1 ? '' : 's'} · {customerPreview.names.length + customerPreview.remaining}{' '}
                customer{customerPreview.names.length + customerPreview.remaining === 1 ? '' : 's'}
              </span>
            </div>
            {customerPreview.names.length === 0 ? (
              <p className="text-xs text-emerald-800">No customers in this batch.</p>
            ) : (
              <p className="text-xs text-emerald-800 leading-tight">
                {customerPreview.names.join(', ')}
                {customerPreview.remaining > 0 && (
                  <span className="text-emerald-700 font-medium"> +{customerPreview.remaining} more</span>
                )}
              </p>
            )}
            <p className="text-[10px] text-emerald-700 mt-1.5 italic">
              Technician assignments and times of day are preserved — only the date shifts.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="bulk-reschedule-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={disabled}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bulk-reschedule-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Moving…
              </>
            ) : (
              `Move ${count || ''} job${count === 1 ? '' : 's'}`.replace(/  +/g, ' ').trim()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkRescheduleModal;
